// src/pages/manager/pchallan/ManagerCreatePChallan.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../utils/api";
import ManagerLayout from "../../../layouts/ManagerLayout";
import SuperAdminLayout from "../../../layouts/SuperAdminLayout";

const ProdCreatePChallan = () => {
  const { state } = useLocation();
  const record = state?.production;
  const superAdminId = JSON.parse(localStorage.getItem("user"))?.id;
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    customer: "FACTORY",
    challanNumber: "",
    date: new Date().toISOString().slice(0, 10),
    items: [],
  });

  useEffect(() => {
    async function init() {
      const [pRes, stockRes, cntRes] = await Promise.all([
        api.get(`/api/products/get-all/${superAdminId}`),
        api.get(`/api/reports/closing-stock/${superAdminId}`),
        api.get(`/api/pchallans/count/${superAdminId}`),
      ]);
      const stockMap = {};
      stockRes.data.forEach((s) => (stockMap[s.productName] = s.closingQty));
      const prods = pRes.data.map((p) => ({
        ...p,
        closingQty: stockMap[p.productName] || 0,
        _secFactor: p.secondaryQuantity || 1,
      }));
      setProducts(prods);
      setForm((f) => ({
        ...f,
        challanNumber: String((cntRes.data.count || 0) + 1).padStart(3, "0"),
      }));
      if (record) {
        setForm((f) => ({
          ...f,
          items: record.items.map((it) => {
            // <-- USE primaryQuantity from production record, not it.quantity
            const prod = prods.find((p) => p.productName === it.product) || {};
            return {
              product: it.product,
              unit: prod.primaryUnit || "",
              availableQty: prod.closingQty || 0,
              secondaryQuantity: it.secondaryQuantity,
              quantity: it.primaryQuantity, // <--- HERE
              price: prod.purchaseRate || 0,
              _secFactor: prod._secFactor || 1,
            };
          }),
        }));
      }
    }
    if (superAdminId) init();
  }, [superAdminId, record]);

  const getTotal = () =>
    form.items.reduce((sum, it) => sum + it.quantity * it.price, 0);

  const handleItemChange = (idx, key, value) => {
    const items = [...form.items];
    const item = { ...items[idx] };

    if (key === "quantity") {
      item.quantity = value;
      item.secondaryQuantity = parseFloat((value / item._secFactor).toFixed(2));
    } else if (key === "secondaryQuantity") {
      item.secondaryQuantity = value;
      item.quantity = parseFloat((value * item._secFactor).toFixed(2));
    } else if (key === "price") {
      item.price = value;
    }

    items[idx] = item;
    setForm({ ...form, items });
  };

  const handleSubmit = async () => {
    try {
      await api.post("/api/pchallans/create", {
        ...form,
        superAdminId,
        total: getTotal(),
      });
      // mark this production as converted
      const converted = JSON.parse(localStorage.getItem("converted") || "[]");
      converted.push(record._id);
      localStorage.setItem("converted", JSON.stringify(converted));

      alert("P-Challan created!");
      navigate("/super-admin/production/view");
    } catch (err) {
      console.error(err);
      alert("Failed to create P-Challan");
    }
  };

  return (
    <SuperAdminLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Convert to Purchase Challan
        </Typography>
        <Box display="flex" gap={2} mb={3}>
          <TextField
            label="Customer"
            value={form.customer}
            disabled
            fullWidth
          />
          <TextField
            label="Challan No"
            value={form.challanNumber}
            disabled
            fullWidth
          />
          <TextField
            type="date"
            label="Date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
        <Paper sx={{ mb: 3 }}>
          <Table>
            <TableBody>
              {form.items.map((it, idx) => (
                <TableRow key={idx}>
                  <TableCell>{it.product}</TableCell>
                  <TableCell>{it.unit}</TableCell>
                  <TableCell>{it.availableQty}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={it.secondaryQuantity}
                      onChange={(e) =>
                        handleItemChange(
                          idx,
                          "secondaryQuantity",
                          Number(e.target.value)
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={it.quantity}
                      onChange={(e) =>
                        handleItemChange(
                          idx,
                          "quantity",
                          Number(e.target.value)
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={it.price}
                      onChange={(e) =>
                        handleItemChange(idx, "price", Number(e.target.value))
                      }
                    />
                  </TableCell>
                  <TableCell>₹{(it.quantity * it.price).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Typography variant="h6">Total: ₹{getTotal().toFixed(2)}</Typography>
        </Box>
        <Box textAlign="center">
          <Button variant="contained" onClick={handleSubmit}>
            Create P-Challan
          </Button>
        </Box>
      </Container>
    </SuperAdminLayout>
  );
};

export default ProdCreatePChallan;
